import { useState, useEffect, useCallback, useRef } from 'react'
import { NotificationService, Notification, NotificationFilters } from '../services/notification-service'
import { GlobalNotificationService } from '../services/global-notification-service'
import { getAccessToken, getCachedUser } from '../services/auth-service'
import type { RealtimeEvent } from '../services/notification-realtime'
import { mergeArrivals, newestCreatedAt, readStateDrifted } from '@/lib/notificationResync'
import { logError, logWarn } from '@/lib/logger'

export interface UseNotificationsReturn {
  notifications: Notification[]
  unreadCount: number
  isLoading: boolean
  error: string | null
  loadNotifications: (filters?: NotificationFilters) => Promise<void>
  markAsRead: (notificationId: string) => Promise<void>
  /** Optimistic; rolls the row back and rejects when the server refuses (#1135). */
  markAsUnread: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (notificationId: string) => Promise<void>
  deleteAllRead: () => Promise<void>
  refreshNotifications: () => Promise<void>
}

export const useNotifications = (initialFilters: NotificationFilters = {}): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<NotificationFilters>(initialFilters)

  // Lade initiale Notifications
  const loadNotifications = useCallback(async (newFilters?: NotificationFilters) => {
    try {
      setIsLoading(true)
      setError(null)

      const token = getAccessToken()

      if (!token) {
        setNotifications([])
        setUnreadCount(0)
        return
      }

      const currentFilters = newFilters || filters
      setFilters(currentFilters)

      const [notificationsData, unreadCountData] = await Promise.all([
        NotificationService.getNotifications(currentFilters),
        NotificationService.getUnreadCount()
      ])

      setNotifications(notificationsData)
      setUnreadCount(unreadCountData)
    } catch (err) {
      console.error('Fehler beim Laden der Notifications:', err)
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler')
    } finally {
      setIsLoading(false)
    }
  }, [filters])

  // Markiere einzelne Notification als gelesen
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const token = getAccessToken()

      if (!token) {
        return
      }


      // Optimistisches Update - sofort im UI anzeigen
      setNotifications(prev => {
        const updated = prev.map(n =>
          n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        )
        const newUnreadCount = updated.filter(n => !n.is_read).length
        setUnreadCount(newUnreadCount)
        return updated
      })

      await NotificationService.markAsRead(notificationId)
    } catch (err) {
      console.error('Fehler beim Markieren als gelesen:', err)
      setError(err instanceof Error ? err.message : 'Fehler beim Markieren als gelesen')
      await loadNotifications()
    }
  }, [loadNotifications])

  // Back to unread (#1135): optimistic, and only this row is rolled back on
  // failure — a full reload would also undo other rows' pending changes.
  const markAsUnread = useCallback(async (notificationId: string) => {
    if (!getAccessToken()) return
    const before = notificationsRef.current.find(n => n.id === notificationId)
    if (!before?.is_read) return
    setNotifications(prev =>
      prev.map(n => (n.id === notificationId ? { ...n, is_read: false, read_at: undefined } : n))
    )
    setUnreadCount(count => count + 1)
    try {
      await NotificationService.markAsUnread(notificationId)
    } catch (err) {
      logError(err as Error, { context: 'useNotifications.markAsUnread', notificationId })
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, is_read: before.is_read, read_at: before.read_at } : n))
      )
      setUnreadCount(count => Math.max(0, count - 1))
      throw err
    }
  }, [])

  // Markiere alle Notifications als gelesen
  const markAllAsRead = useCallback(async () => {
    try {
      const token = getAccessToken()

      if (!token) {
        return
      }


      setNotifications(prev => {
        const updated = prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
        setUnreadCount(0)
        return updated
      })

      await NotificationService.markAllAsRead()
    } catch (err) {
      console.error('Fehler beim Markieren aller als gelesen:', err)
      setError(err instanceof Error ? err.message : 'Fehler beim Markieren aller als gelesen')
      await loadNotifications()
    }
  }, [loadNotifications])

  // Lösche einzelne Notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const token = getAccessToken()

      if (!token) {
        return
      }


      setNotifications(prev => {
        const notification = prev.find(n => n.id === notificationId)
        const updated = prev.filter(n => n.id !== notificationId)
        if (notification && !notification.is_read) {
          setUnreadCount(count => Math.max(0, count - 1))
        }
        return updated
      })

      await NotificationService.deleteNotification(notificationId)
    } catch (err) {
      console.error('Fehler beim Löschen der Notification:', err)
      setError(err instanceof Error ? err.message : 'Fehler beim Löschen der Notification')
      await loadNotifications()
    }
  }, [loadNotifications])

  // Lösche alle gelesenen Notifications
  const deleteAllRead = useCallback(async () => {
    try {
      const token = getAccessToken()

      if (!token) {
        return
      }


      setNotifications(prev => {
        const updated = prev.filter(n => !n.is_read)
        return updated
      })

      await NotificationService.deleteAllRead()
    } catch (err) {
      console.error('Fehler beim Löschen aller gelesenen Notifications:', err)
      setError(err instanceof Error ? err.message : 'Fehler beim Löschen aller gelesenen Notifications')
      await loadNotifications()
    }
  }, [loadNotifications])

  const refreshNotifications = useCallback(async () => {
    await loadNotifications()
  }, [loadNotifications])

  // Kept in refs so the realtime callbacks, registered once, see the latest state.
  const notificationsRef = useRef<Notification[]>([])
  const unreadCountRef = useRef(0)
  const filtersRef = useRef<NotificationFilters>(filters)
  notificationsRef.current = notifications
  unreadCountRef.current = unreadCount
  filtersRef.current = filters

  // Full reload without the loading state — a re-sync must not flash the panel.
  const reloadQuietly = useCallback(async () => {
    const [list, unread] = await Promise.all([
      NotificationService.getNotifications(filtersRef.current),
      NotificationService.getUnreadCount()
    ])
    setNotifications(list)
    setUnreadCount(unread)
    GlobalNotificationService.setNotifications(list, unread)
  }, [])

  // After every realtime (re)connect (#1090): fetch what arrived since the
  // newest known notification; reload everything if read states drifted too.
  const resync = useCallback(async () => {
    if (!getAccessToken()) return
    try {
      const current = notificationsRef.current
      const since = newestCreatedAt(current)
      if (!since || Object.keys(filtersRef.current).length > 0) {
        await reloadQuietly()
        return
      }
      const { notifications: arrivals, unreadCount: serverUnread } =
        await NotificationService.getNotificationsSince(since)
      if (readStateDrifted(unreadCountRef.current, current, arrivals, serverUnread)) {
        await reloadQuietly()
        return
      }
      if (arrivals.length === 0) return
      const merged = mergeArrivals(notificationsRef.current, arrivals)
      setNotifications(merged)
      setUnreadCount(serverUnread)
      GlobalNotificationService.setNotifications(merged, serverUnread)
    } catch (err) {
      logWarn({ context: 'useNotifications' }, `Notification re-sync failed: ${String(err)}`)
    }
  }, [reloadQuietly])

  const applyRealtimeEvent = useCallback((event: RealtimeEvent) => {
    const notification = event.notification
    if (event.eventType === 'SYNC' || !notification) {
      // A bulk change in another session (mark-all-read, delete-all-read).
      reloadQuietly().catch((err) =>
        logWarn({ context: 'useNotifications' }, `Reload after SYNC failed: ${String(err)}`)
      )
      return
    }
    setNotifications(prev => {
      let nextList: Notification[]
      if (event.eventType === 'DELETE') {
        nextList = prev.filter(n => n.id !== notification.id)
        GlobalNotificationService.removeNotification(notification.id)
      } else {
        const idx = prev.findIndex(n => n.id === notification.id)
        if (idx >= 0) {
          nextList = [...prev]
          nextList[idx] = notification
          GlobalNotificationService.updateNotification(notification)
        } else {
          nextList = [notification, ...prev]
          GlobalNotificationService.addNotification(notification)
        }
      }
      setUnreadCount(nextList.filter(n => !n.is_read).length)
      return nextList
    })
  }, [reloadQuietly])

  // Realtime subscription (Web PubSub).
  useEffect(() => {
    let isMounted = true
    let subscriptionId: string | null = null

    const setup = async () => {
      if (!getAccessToken() || !getCachedUser()) return
      // Give the auth session a moment to settle after sign-in.
      await new Promise(resolve => setTimeout(resolve, 500))
      if (!isMounted) return
      const id = await NotificationService.subscribeToNotifications({
        onEvent: (event) => {
          if (isMounted) applyRealtimeEvent(event)
        },
        onConnected: () => {
          if (isMounted) void resync()
        },
      })
      if (isMounted) {
        subscriptionId = id
      } else {
        void NotificationService.unsubscribeFromNotifications(id)
      }
    }

    setup().catch((err) =>
      logWarn({ context: 'useNotifications' }, `Realtime subscription failed: ${String(err)}`)
    )

    return () => {
      isMounted = false
      if (subscriptionId) {
        void NotificationService.unsubscribeFromNotifications(subscriptionId)
        subscriptionId = null
      }
    }
  }, [applyRealtimeEvent, resync])

  // Initiale Daten laden
  useEffect(() => {
    loadNotifications()
  }, [loadNotifications])

  // Globales Event für Notifications beim Login
  useEffect(() => {
    const handleNotificationsLoaded = (event: CustomEvent) => {
      const { notifications, unreadCount } = event.detail

      setNotifications(notifications)
      setUnreadCount(unreadCount)
      setIsLoading(false)
      setError(null)
    }

    window.addEventListener('notifications-loaded', handleNotificationsLoaded as EventListener)

    return () => {
      window.removeEventListener('notifications-loaded', handleNotificationsLoaded as EventListener)
    }
  }, [])

  // Synchronisation mit dem globalen Notification Service
  useEffect(() => {
    const handleGlobalUpdate = (notifications: Notification[], unreadCount: number) => {
      setNotifications(notifications)
      setUnreadCount(unreadCount)
    }

    const cleanup = GlobalNotificationService.addListener(handleGlobalUpdate)
    return cleanup
  }, [])

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    loadNotifications,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
    refreshNotifications
  }
}
